import { EntityState } from '@reduxjs/toolkit'
import { CommonState, Model } from '@/core/classes/Model'
import { Patient } from './patient.model'
import { Staff } from './staff.model'
import { Consultation } from './consultation.model'
import { Expertise } from './expertise.model'
import { SearchResponse } from '../types/response'
import { Document } from './document.model'

export enum FormFieldType {
    Text = 'text',
    TextArea = 'textarea',
    Multiple = 'multiple',
    Number = 'number',
    Boolean = 'boolean',
    Date = 'date',
    DateTime = 'datetime'
}

export enum FormSubmissionStatus {
    Draft = 'draft',
    Submitted = 'submitted'
}

// Marks special-purpose submissions (e.g. waiting-room readiness checklists); generic forms leave it unset
export enum FormSubmissionPurpose {
    ReadinessChecklist = 'readiness-checklist'
}

// FormTemplate category identifying templates usable as waiting-room preparation checklists
export const READINESS_CHECKLIST_CATEGORY = 'readiness-checklist'

export interface FormField {
    id: string
    label: string
    type: FormFieldType
    required: boolean
    options?: string[]
}

export interface FormTemplate {
    _id: string
    title: string
    description?: string
    fields: FormField[]
    categories: string[]
    tags: string[]
    isActive: boolean
    createdBy: Staff
    lastUpdatedBy: Staff
    createdAt: string
    updatedAt: string
    version: number
    aiOutputConfig?: AIOutputConfig
    pdfFields?: PdfFieldDto[]
    fillablePdf: Document
}

export interface AIOutputConfig {
    enabled: boolean
    prompt?: string
    outputFilenameTemplate?: string
}

export interface PdfFieldDto {
    type: string
    label: string
    x: number
    y: number
    width: number
    height: number
    page: number
    fontSize?: number
    multiline?: boolean
}

export interface FormResponse {
    fieldId: string
    value: any
}

export interface FormSubmission {
    _id: string
    template: FormTemplate
    patient: Patient
    consultation?: Consultation
    expertise?: Expertise
    responses: FormResponse[]
    status: FormSubmissionStatus
    purpose?: FormSubmissionPurpose
    submittedBy: Staff
    tags: string[]
    submittedAt?: string
    createdAt: string
    updatedAt: string
}

// DTOs
export interface CreateFormTemplateDto {
    title: string
    description?: string
    fields: Omit<FormField, '_id'>[]
    categories?: string[]
    tags?: string[]
    isActive?: boolean
    aiOutputConfig?: AIOutputConfig
    pdfFields?: PdfFieldDto[]
}

export interface UpdateFormTemplateDto {
    title?: string
    description?: string
    fields?: Omit<FormField, '_id'>[]
    categories?: string[]
    tags?: string[]
    isActive?: boolean
    aiOutputConfig?: AIOutputConfig
    pdfFields?: PdfFieldDto[]
}

export interface CreateFormSubmissionDto {
    template: string
    patient: string
    consultation?: string
    expertise?: string
    responses: FormResponse[]
    status?: FormSubmissionStatus
    purpose?: FormSubmissionPurpose
    tags?: string[]
}

export interface UpdateFormSubmissionDto {
    responses?: FormResponse[]
    status?: FormSubmissionStatus
    tags?: string[]
}

// Search DTOs
export interface SearchFormTemplateDto {
    searchText?: string
    categories?: string[]
    tags?: string[]
    isActive?: boolean
}

export interface SearchFormSubmissionDto {
    template?: string
    patient?: string
    consultation?: string
    expertise?: string
    status?: FormSubmissionStatus
    purpose?: FormSubmissionPurpose
    tags?: string[]
    startDate?: string
    endDate?: string
}

// Response Types
export type FormTemplateResponse = SearchResponse<FormTemplate>
export type FormSubmissionResponse = SearchResponse<FormSubmission>

export type FormState = CommonState & {
    formTemplates: EntityState<FormTemplate, FormTemplate['_id']>
    formSubmissions: EntityState<FormSubmission, FormSubmission['_id']>
}

// Statistics Types
export interface FormFieldStatistics {
    fieldId: string
    data: Record<string, number>
    totalFieldResponses: number
}

export interface FormStatistics {
    templateId: string
    totalSubmissions: number
    fields: FormFieldStatistics[]
}

class FormModel extends Model {
    static instance: FormModel

    static getInstance() {
        if (!FormModel.instance) {
            FormModel.instance = new FormModel('form')
        }
        return FormModel.instance
    }

    getInitialState(): FormState {
        return {
            ...this.commonState,
            formTemplates: { entities: {}, ids: [] },
            formSubmissions: { entities: {}, ids: [] }
        }
    }
}

export default FormModel.getInstance() 